import type { DNSRecord } from '../types';

import { DNSValidationError } from './DNSValidationError';
import { WNextDNS } from './wnext';

export { DNSValidationError };

export enum DNSReturnCode {
  NoError = 0,
  // not currently used
  // FormErr = 1,
  // ServFail = 2,
  // NXDomain = 3,
  // NotImp = 4,
  // Refused = 5,
  // YXDomain = 6,
  // YXRRSet = 7,
  // NXRRSet = 8,
  // NotAuth = 9,
  // NotZone = 10,
  // DSOTYPENI = 11,
  // BADVERS = 16,
  // // this is allowed as per spec
  // // https://www.iana.org/assignments/dns-parameters/dns-parameters.xhtml#dns-parameters-6
  // // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  // BADSIG = 16,
  // BADKEY = 17,
  // BADTIME = 18,
  // BADMODE = 19,
  // BADNAME = 20,
  // BADALG = 21,
  // BADTRUNC = 22,
  // BADCOOKIE = 23,
}

export enum DNSRecordType {
  // unused types commented for now
  A = 1,
  // NS = 2,
  CNAME = 5,
  SOA = 6,
  // PTR = 12,
  // MX = 15,
  // TXT = 16,
  AAAA = 28,
  // SRV = 33,
  // SPF = 99,
  // ANY = 255,
}

interface DNSQuestion {
  name: string;
  type: DNSRecordType;
}

interface DNSAnswer {
  name: string;
  type: DNSRecordType;
  TTL: number;
  data: string;
}

interface DNSResponse {
  Status: DNSReturnCode;
  TC: boolean;
  RD: boolean;
  RA: boolean;
  AD: boolean;
  CD: boolean;
  Question: DNSQuestion[];
  Answer?: DNSAnswer[];
  Authority?: DNSAnswer[];
}

const providers = {
  cloudflare: 'https://cloudflare-dns.com/dns-query',
  google: 'https://dns.google/resolve',
};

export async function queryDNS(
  domain: string,
  type: string,
  provider: 'google' | 'cloudflare' = 'cloudflare'
): Promise<DNSResponse> {
  const providerUrl = providers[provider];
  if (!providerUrl) {
    throw new Error(`Unknown DNS provider: ${provider}`);
  }
  const resp = await fetch(
    `${providerUrl}?name=${encodeURIComponent(domain)}&type=${encodeURIComponent(type)}&_cache=${Date.now()}`,
    { headers: { Accept: 'application/dns-json' } }
  );
  return resp.json();
}

/**
 * Checks if the CNAME record for a domain points to a specific target domain.
 *
 * @param hostname The domain to check.
 * @param targetHostname The target domain the CNAME should point to.
 */
export async function checkCNAME(
  hostname: string,
  targetHostname: string
): Promise<'cname' | 'soa'> {
  const record = await queryDNS(hostname, 'CNAME');
  if (record.Status !== DNSReturnCode.NoError) {
    const code = DNSReturnCode[record.Status] || `code ${record.Status}`;
    throw new DNSValidationError(`DNS lookup failed, return code: ${code}`);
  }

  if (!record.Answer || record.Answer.length === 0) {
    // check if hostname is an apex record
    const apexRecord = await queryDNS(hostname, 'SOA');
    if (apexRecord.Status !== DNSReturnCode.NoError) {
      const code = DNSReturnCode[apexRecord.Status] || `code ${apexRecord.Status}`;
      throw new DNSValidationError(`DNS lookup failed, return code: ${code}`);
    }
    if (
      apexRecord.Answer &&
      apexRecord.Answer.length === 1 &&
      apexRecord.Answer?.[0].type === DNSRecordType.SOA
    ) {
      return 'soa';
    }
    throw new DNSValidationError(
      'DNS response contains no answers, record is probably not a CNAME'
    );
  }

  const answer = record.Answer[0];
  const cnameTarget = answer.data.replace(/\.$/, '');
  if (cnameTarget !== targetHostname) {
    throw new DNSValidationError(
      `CNAME does not point at ${targetHostname}, instead points at ${cnameTarget}`
    );
  }
  return 'cname';
}

export async function isARecordPropagated(
  host: string,
  provider: 'cloudflare' | 'google' | 'wnext'
): Promise<[boolean, string]> {
  if (provider === 'wnext') {
    const res: DNSRecord = await new WNextDNS().getRecord({ name: host });
    return [res.status === 'propagated', res.status];
  }
  const rec = await queryDNS(host, 'A', provider);
  if (rec.Status !== DNSReturnCode.NoError) {
    return [false, 'error'];
  }
  if (!rec.Answer || rec.Answer.length === 0) {
    return [false, 'no answer'];
  }
  const lastAnswer = rec.Answer[rec.Answer.length - 1];
  if (
    !(lastAnswer.type === DNSRecordType.A || lastAnswer.type === DNSRecordType.AAAA) ||
    !lastAnswer.data
  ) {
    return [false, 'no a record'];
  }
  return [true, 'propagated'];
}

/**
 * Tests if a hostname has fully propagated across all DNS providers (Cloudflare, Google, and WNext).
 *
 * @param host
 * @returns
 */
export async function isFullyPropagated(host: string): Promise<boolean> {
  try {
    return (
      await Promise.all([
        isARecordPropagated(host, 'cloudflare'),
        isARecordPropagated(host, 'google'),
        isARecordPropagated(host, 'wnext'),
      ])
    ).every((r) => !!r[0]);
  } catch {
    return false;
  }
}
